import m from './m.js'

export const DONE = '__done__'

/** Lane name of a card: closed → Done, else the lane the server put it in (labels or stored). */
export function laneOf(card) {
  return card.state === 'open' ? card.lane : DONE
}

/** Cards of a lane in display order: new (unpositioned) cards on top, then manual order. Done is newest first. */
export function laneCards(data, lane) {
  const cards = data.cards.filter(card => laneOf(card) === lane)
  if (lane === DONE)
    return cards.sort((a, b) => b.closedAt.localeCompare(a.closedAt))
  const pos = data.positions
  return cards.sort((a, b) => {
    const pa = pos[a.number]
    const pb = pos[b.number]
    if (pa === undefined && pb === undefined)
      return b.createdAt.localeCompare(a.createdAt)
    if (pa === undefined) return -1
    if (pb === undefined) return 1
    return pa - pb
  })
}

export const FILTERS = ['q', 'type', 'label', 'assignee', 'milestone']

/** Active filters from the URL query. */
export function filterQuery() {
  // no router yet (e.g. in tests): no filters
  const params = m.route.param() ?? {}
  return Object.fromEntries(
    FILTERS.filter(k => params[k]).map(k => [k, params[k]])
  )
}

/** Values of a multi-select filter, stored comma-separated in the URL. */
export function filterValues(name) {
  return (filterQuery()[name] || '').split(',').filter(Boolean)
}

export function setFilter(name, value) {
  const query = { ...filterQuery(), [name]: value || undefined }
  if (!value) delete query[name]
  m.route.set(m.route.get().split('?')[0], query, { replace: true })
}

/**
 * An open issue whose work shows on the board as an open pull request linked to it.
 * The link is checked on both cards: live updates refetch a new PR, not the issue it closes.
 */
export function hasOpenPr(card, cards) {
  return (
    card.type === 'issue' &&
    card.state === 'open' &&
    cards.some(
      c =>
        c.type === 'pr' &&
        c.state === 'open' &&
        (card.links.includes(c.number) || c.links.includes(card.number))
    )
  )
}

/** Within a filter any selected value matches; all filters must match. */
export function matches(card, filter) {
  const any = (name, test) =>
    !filter[name] || filter[name].split(',').some(test)
  if (filter.q) {
    const q = filter.q.toLowerCase().trim()
    if (
      !card.title.toLowerCase().includes(q) &&
      `#${card.number}` !== q &&
      String(card.number) !== q
    )
      return false
  }
  return (
    any('type', type => card.type === type) &&
    any('label', name => card.labels.some(l => l.name === name)) &&
    any('assignee', login =>
      login === '-'
        ? card.assignees.length === 0
        : card.assignees.some(a => a.login === login)
    ) &&
    any('milestone', title => card.milestone?.title === title)
  )
}
